/**
 * <stln-editorial-grid>
 *
 * Measures the image area of the first regular card in a grid and writes it to
 * --stln-editorial-media-height on the grid element. Editorial tiles
 * (blocks/_stln-editorial-tile.liquid) use that value as their frame height, so
 * every tile is exactly as tall as a card image. Re-measures when the card
 * resizes and when the grid contents are replaced (filtering, sorting, editor).
 *
 * Attributes (CSS selectors):
 *   data-grid   the grid element               default: .product-grid
 *   data-item   a regular card (direct child)   default: [ref="cards[]"]
 *   data-media  the image area inside the card  default: [ref="cardGallery"], .card-gallery
 */
if (!customElements.get('stln-editorial-grid')) {
  class StlnEditorialGrid extends HTMLElement {
    connectedCallback() {
      this.scheduleMeasure = this.scheduleMeasure.bind(this);
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.mutationObserver = new MutationObserver(this.scheduleMeasure);
      this.mutationObserver.observe(this, { childList: true, subtree: true });
      window.addEventListener('resize', this.scheduleMeasure);
      this.measure();
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      this.mutationObserver.disconnect();
      window.removeEventListener('resize', this.scheduleMeasure);
    }

    get gridSelector() {
      return this.dataset.grid || '.product-grid';
    }

    get itemSelector() {
      return this.dataset.item || '[ref="cards[]"]';
    }

    get mediaSelector() {
      return this.dataset.media || '[ref="cardGallery"], .card-gallery';
    }

    scheduleMeasure() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    }

    measure() {
      const grid = this.querySelector(this.gridSelector);
      if (!grid) return;

      const card = grid.querySelector(`:scope > :is(${this.itemSelector})`);
      const media = card && card.querySelector(this.mediaSelector);

      if (card !== this.observedCard) {
        this.resizeObserver.disconnect();
        if (card) this.resizeObserver.observe(card);
        this.observedCard = card;
      }

      if (!card || !media || !card.offsetParent) {
        grid.style.removeProperty('--stln-editorial-media-height');
        return;
      }

      const height = media.getBoundingClientRect().bottom - card.getBoundingClientRect().top;
      if (height > 0) {
        grid.style.setProperty('--stln-editorial-media-height', `${Math.round(height * 100) / 100}px`);
      }
    }
  }

  customElements.define('stln-editorial-grid', StlnEditorialGrid);
}
